import axios from "axios";
import { forceAuthLogout, getFreshAccessToken, isTokenExpiredResponse, refreshAccessToken } from "./authTokenService";

const instance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

instance.defaults.headers.common["Content-Type"] = "application/x-www-form-urlencoded";

const setAuthorizationHeader = (headers, token) => {
  if (!token) return;
  if (typeof headers?.setAuthorization === "function") {
    headers.setAuthorization(`Bearer ${token}`);
    return;
  }
  headers.Authorization = `Bearer ${token}`;
};

const retryWithFreshToken = async (request) => {
  if (!request || request._retryWithFreshToken) {
    forceAuthLogout();
    return Promise.reject(new Error("Session expired"));
  }

  request._retryWithFreshToken = true;
  const token = await refreshAccessToken();
  setAuthorizationHeader(request.headers, token);
  return instance(request);
};

instance.interceptors.request.use(
  async (request) => {
    const token = await getFreshAccessToken({ optional: true });
    const language = localStorage.getItem("language_preferred");
    if (token) {
      setAuthorizationHeader(request.headers, token);

      if (["/uploadAsset"].includes(request?.url)) {
        request.headers["Content-Type"] = "multipart/form-data";
      }
    }
    request.data = {
      mobile: false,
      ...request.data,
    };
    if (!["/language/read", "/language/list"].includes(request?.url)) {
      request.data = {
        ...request.data,
        language_code: language || "en",
      };
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  async (response) => {
    if (isTokenExpiredResponse(response)) {
      try {
        return await retryWithFreshToken(response.config);
      } catch (error) {
        forceAuthLogout();
        return Promise.reject(error);
      }
    }
    return response;
  },
  async (error) => {
    if (isTokenExpiredResponse(error?.response)) {
      try {
        return await retryWithFreshToken(error.config);
      } catch (refreshError) {
        forceAuthLogout();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
